import React, { useState, useEffect } from 'react';
import { 
  Layout, Eye, Save, Plus, Edit, Trash2, X, Download, 
  Type, Image as ImageIcon, Palette, Maximize, Settings,
  AlignLeft, AlignCenter, AlignRight, FileText, Upload
} from 'lucide-react';
import { apiCall } from './Api';

const InvoiceTemplateDesigner = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDesigner, setShowDesigner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Template configuration state
  const [config, setConfig] = useState({
    template_name: 'New Template',
    host_tenant_id: null, // null = global template
    
    // Header
    header_logo_url: '',
    header_text: 'TAX INVOICE',
    show_company_logo: true,
    logo_position: 'left',
    header_height: 100,
    
    // Footer
    footer_text: 'Thank you for your business!',
    footer_height: 50,
    
    // Display options
    show_billing_address: true,
    show_payment_terms: true,
    show_bank_details: false,
    
    // Styling
    paper_size: 'A4',
    font_family: 'Helvetica',
    primary_color: '#2563eb',
    secondary_color: '#64748b',
    
    // Custom fields (JSON)
    custom_fields: {}
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/billing/invoice-templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      const url = selectedTemplate 
        ? `/api/billing/invoice-templates/${selectedTemplate.id}`
        : '/api/billing/invoice-templates';
      
      const method = selectedTemplate ? 'PUT' : 'POST';
      
      const response = await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        alert('Template saved successfully');
        setShowDesigner(false);
        loadTemplates();
      } else {
        alert('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const response = await apiCall(`/api/billing/invoice-templates/${templateId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setConfig({
      template_name: 'New Template',
      host_tenant_id: null,
      header_logo_url: '',
      header_text: 'TAX INVOICE',
      show_company_logo: true,
      logo_position: 'left',
      header_height: 100,
      footer_text: 'Thank you for your business!',
      footer_height: 50,
      show_billing_address: true,
      show_payment_terms: true,
      show_bank_details: false,
      paper_size: 'A4',
      font_family: 'Helvetica',
      primary_color: '#2563eb',
      secondary_color: '#64748b',
      custom_fields: {}
    });
    setShowDesigner(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setConfig({
      template_name: template.template_name,
      host_tenant_id: template.host_tenant_id,
      header_logo_url: template.header_logo_url || '',
      header_text: template.header_text || 'TAX INVOICE',
      show_company_logo: template.show_company_logo,
      logo_position: template.logo_position || 'left',
      header_height: template.header_height || 100,
      footer_text: template.footer_text || '',
      footer_height: template.footer_height || 50,
      show_billing_address: template.show_billing_address,
      show_payment_terms: template.show_payment_terms,
      show_bank_details: template.show_bank_details || false,
      paper_size: template.paper_size || 'A4',
      font_family: template.font_family || 'Helvetica',
      primary_color: template.primary_color || '#2563eb',
      secondary_color: template.secondary_color || '#64748b',
      custom_fields: template.custom_fields || {}
    });
    setShowDesigner(true);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Invoice Template Designer</h2>
          <p className="text-sm text-gray-600 mt-1">Create and customize professional invoice templates</p>
        </div>
        <button
          onClick={handleNewTemplate}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span>New Template</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No templates yet</p>
          <button
            onClick={handleNewTemplate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div key={template.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{template.template_name}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {template.host_tenant_id ? 'Your template' : 'Global template'}{template.editable === false ? ' · read-only' : ''}
                  </p>
                </div>
                {template.is_default && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    Default
                  </span>
                )}
              </div>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-gray-400" />
                  <div className="flex gap-1">
                    <div 
                      className="w-4 h-4 rounded border"
                      style={{ backgroundColor: template.primary_color }}
                    />
                    <div 
                      className="w-4 h-4 rounded border"
                      style={{ backgroundColor: template.secondary_color }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Type className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{template.font_family}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Maximize className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{template.paper_size}</span>
                </div>
              </div>

              {template.editable !== false && <div className="flex gap-2">
                <button
                  onClick={() => handleEditTemplate(template)}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm flex items-center justify-center gap-1"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteTemplate(template.id)}
                  className="px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>}
            </div>
          ))}
        </div>
      )}

      {/* Template Designer Modal */}
      {showDesigner && (
        <TemplateDesignerModal
          config={config}
          setConfig={setConfig}
          onSave={handleSaveTemplate}
          onClose={() => setShowDesigner(false)}
          isEditing={!!selectedTemplate}
        />
      )}
    </div>
  );
};

// Template Designer Modal Component
const TemplateDesignerModal = ({ config, setConfig, onSave, onClose, isEditing }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [showPreview, setShowPreview] = useState(false);

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'header', label: 'Header', icon: Layout },
    { id: 'footer', label: 'Footer', icon: AlignLeft },
    { id: 'styling', label: 'Styling', icon: Palette },
    { id: 'display', label: 'Display', icon: Eye },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            {isEditing ? 'Edit' : 'Create'} Invoice Template
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              {showPreview ? 'Hide' : 'Show'} Preview
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Configuration */}
          <div className="w-1/2 border-r overflow-y-auto">
            {/* Tabs */}
            <div className="border-b bg-gray-50 px-4">
              <nav className="flex space-x-4 overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-3 px-3 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6 space-y-4">
              {activeTab === 'general' && (
                <GeneralTab config={config} setConfig={setConfig} />
              )}
              {activeTab === 'header' && (
                <HeaderTab config={config} setConfig={setConfig} />
              )}
              {activeTab === 'footer' && (
                <FooterTab config={config} setConfig={setConfig} />
              )}
              {activeTab === 'styling' && (
                <StylingTab config={config} setConfig={setConfig} />
              )}
              {activeTab === 'display' && (
                <DisplayTab config={config} setConfig={setConfig} />
              )}
            </div>
          </div>

          {/* Right Panel - Live Preview */}
          <div className="w-1/2 overflow-y-auto bg-gray-100 p-6">
            {showPreview ? (
              <InvoicePreview config={config} />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <Eye className="w-16 h-16 mx-auto mb-3" />
                  <p>Enable preview to see live changes</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Template
          </button>
        </div>
      </div>
    </div>
  );
};

// General Tab
const GeneralTab = ({ config, setConfig }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Template Name *
      </label>
      <input
        type="text"
        value={config.template_name}
        onChange={(e) => setConfig({ ...config, template_name: e.target.value })}
        className="w-full px-3 py-2 border rounded-lg"
        placeholder="e.g., Professional Blue"
      />
    </div>

    <div>
      <p className="text-xs text-gray-500">
        Templates you create are yours; platform templates are shared read-only.
      </p>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Paper Size
      </label>
      <select
        value={config.paper_size}
        onChange={(e) => setConfig({ ...config, paper_size: e.target.value })}
        className="w-full px-3 py-2 border rounded-lg"
      >
        <option value="A4">A4 (210 × 297 mm)</option>
        <option value="Letter">Letter (8.5 × 11 in)</option>
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Font Family
      </label>
      <select
        value={config.font_family}
        onChange={(e) => setConfig({ ...config, font_family: e.target.value })}
        className="w-full px-3 py-2 border rounded-lg"
      >
        <option value="Helvetica">Helvetica</option>
        <option value="Times-Roman">Times New Roman</option>
        <option value="Courier">Courier</option>
      </select>
    </div>
  </div>
);

// Header Tab
const HeaderTab = ({ config, setConfig }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Header Text
      </label>
      <input
        type="text"
        value={config.header_text}
        onChange={(e) => setConfig({ ...config, header_text: e.target.value })}
        className="w-full px-3 py-2 border rounded-lg"
        placeholder="TAX INVOICE"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Logo URL
      </label>
      <input
        type="url"
        value={config.header_logo_url}
        onChange={(e) => setConfig({ ...config, header_logo_url: e.target.value })}
        className="w-full px-3 py-2 border rounded-lg"
        placeholder="https://example.com/logo.png"
      />
      <p className="text-xs text-gray-500 mt-1">
        Optional: URL to company logo image
      </p>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Logo Position
      </label>
      <div className="flex gap-2">
        {['left', 'center', 'right'].map((pos) => (
          <button
            key={pos}
            onClick={() => setConfig({ ...config, logo_position: pos })}
            className={`flex-1 px-3 py-2 border rounded-lg capitalize ${
              config.logo_position === pos
                ? 'bg-blue-50 border-blue-500 text-blue-700'
                : 'bg-white text-gray-700'
            }`}
          >
            {pos === 'left' && <AlignLeft className="w-4 h-4 mx-auto" />}
            {pos === 'center' && <AlignCenter className="w-4 h-4 mx-auto" />}
            {pos === 'right' && <AlignRight className="w-4 h-4 mx-auto" />}
          </button>
        ))}
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Header Height (pixels)
      </label>
      <input
        type="number"
        min="50"
        max="200"
        value={config.header_height}
        onChange={(e) => setConfig({ ...config, header_height: parseInt(e.target.value) })}
        className="w-full px-3 py-2 border rounded-lg"
      />
    </div>

    <div className="flex items-center">
      <input
        type="checkbox"
        id="show_company_logo"
        checked={config.show_company_logo}
        onChange={(e) => setConfig({ ...config, show_company_logo: e.target.checked })}
        className="w-4 h-4 text-blue-600 rounded"
      />
      <label htmlFor="show_company_logo" className="ml-2 text-sm text-gray-700">
        Show company logo
      </label>
    </div>
  </div>
);

// Footer Tab
const FooterTab = ({ config, setConfig }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Footer Text
      </label>
      <textarea
        value={config.footer_text}
        onChange={(e) => setConfig({ ...config, footer_text: e.target.value })}
        className="w-full px-3 py-2 border rounded-lg"
        rows="3"
        placeholder="Thank you for your business!"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Footer Height (pixels)
      </label>
      <input
        type="number"
        min="30"
        max="100"
        value={config.footer_height}
        onChange={(e) => setConfig({ ...config, footer_height: parseInt(e.target.value) })}
        className="w-full px-3 py-2 border rounded-lg"
      />
    </div>
  </div>
);

// Styling Tab
const StylingTab = ({ config, setConfig }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Primary Color
      </label>
      <div className="flex gap-2">
        <input
          type="color"
          value={config.primary_color}
          onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
          className="w-16 h-10 border rounded cursor-pointer"
        />
        <input
          type="text"
          value={config.primary_color}
          onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
          className="flex-1 px-3 py-2 border rounded-lg font-mono"
          placeholder="#2563eb"
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Used for headers and accents
      </p>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Secondary Color
      </label>
      <div className="flex gap-2">
        <input
          type="color"
          value={config.secondary_color}
          onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
          className="w-16 h-10 border rounded cursor-pointer"
        />
        <input
          type="text"
          value={config.secondary_color}
          onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
          className="flex-1 px-3 py-2 border rounded-lg font-mono"
          placeholder="#64748b"
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Used for borders and secondary text
      </p>
    </div>

    <div className="border-t pt-4">
      <h4 className="font-medium text-gray-900 mb-3">Quick Presets</h4>
      <div className="grid grid-cols-3 gap-2">
        <ColorPreset 
          name="Blue"
          primary="#2563eb"
          secondary="#64748b"
          onClick={() => setConfig({ 
            ...config, 
            primary_color: '#2563eb',
            secondary_color: '#64748b'
          })}
        />
        <ColorPreset 
          name="Green"
          primary="#059669"
          secondary="#6b7280"
          onClick={() => setConfig({ 
            ...config, 
            primary_color: '#059669',
            secondary_color: '#6b7280'
          })}
        />
        <ColorPreset 
          name="Purple"
          primary="#7c3aed"
          secondary="#6b7280"
          onClick={() => setConfig({ 
            ...config, 
            primary_color: '#7c3aed',
            secondary_color: '#6b7280'
          })}
        />
        <ColorPreset 
          name="Red"
          primary="#dc2626"
          secondary="#6b7280"
          onClick={() => setConfig({ 
            ...config, 
            primary_color: '#dc2626',
            secondary_color: '#6b7280'
          })}
        />
        <ColorPreset 
          name="Orange"
          primary="#ea580c"
          secondary="#6b7280"
          onClick={() => setConfig({ 
            ...config, 
            primary_color: '#ea580c',
            secondary_color: '#6b7280'
          })}
        />
        <ColorPreset 
          name="Teal"
          primary="#0d9488"
          secondary="#6b7280"
          onClick={() => setConfig({ 
            ...config, 
            primary_color: '#0d9488',
            secondary_color: '#6b7280'
          })}
        />
      </div>
    </div>
  </div>
);

const ColorPreset = ({ name, primary, secondary, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center p-2 border rounded hover:border-blue-500 transition"
  >
    <div className="flex gap-1 mb-1">
      <div 
        className="w-6 h-6 rounded border"
        style={{ backgroundColor: primary }}
      />
      <div 
        className="w-6 h-6 rounded border"
        style={{ backgroundColor: secondary }}
      />
    </div>
    <span className="text-xs text-gray-600">{name}</span>
  </button>
);

// Display Tab
const DisplayTab = ({ config, setConfig }) => (
  <div className="space-y-4">
    <div>
      <h4 className="font-medium text-gray-900 mb-3">Show/Hide Sections</h4>
      <div className="space-y-2">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="show_billing_address"
            checked={config.show_billing_address}
            onChange={(e) => setConfig({ ...config, show_billing_address: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <label htmlFor="show_billing_address" className="ml-2 text-sm text-gray-700">
            Show Billing Address
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="show_payment_terms"
            checked={config.show_payment_terms}
            onChange={(e) => setConfig({ ...config, show_payment_terms: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <label htmlFor="show_payment_terms" className="ml-2 text-sm text-gray-700">
            Show Payment Terms
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="show_bank_details"
            checked={config.show_bank_details}
            onChange={(e) => setConfig({ ...config, show_bank_details: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <label htmlFor="show_bank_details" className="ml-2 text-sm text-gray-700">
            Show Bank Details
          </label>
        </div>
      </div>
    </div>
  </div>
);

// Live Invoice Preview
const InvoicePreview = ({ config }) => {
  const sampleInvoice = {
    invoice_number: 'INV-2025-001',
    invoice_date: '2025-12-29',
    due_date: '2026-01-28',
    company_name: 'Your Company Name',
    company_address: '123 Business Street',
    company_city: 'Business City, ST 12345',
    customer_name: 'Customer ABC',
    customer_address: '456 Customer Ave, Customer City, ST 67890',
    items: [
      { description: 'Service Item 1', quantity: 10, rate: 1000, amount: 10000 },
      { description: 'Service Item 2', quantity: 5, rate: 500, amount: 2500 },
    ],
    subtotal: 12500,
    tax: 2250,
    total: 14750
  };

  return (
    <div 
      className="bg-white shadow-lg mx-auto"
      style={{ 
        width: config.paper_size === 'A4' ? '210mm' : '8.5in',
        minHeight: config.paper_size === 'A4' ? '297mm' : '11in',
        fontFamily: config.font_family,
        fontSize: '12px'
      }}
    >
      {/* Header */}
      <div 
        className="p-6 border-b-2"
        style={{ 
          height: `${config.header_height}px`,
          borderColor: config.primary_color,
          backgroundColor: config.primary_color + '10'
        }}
      >
        <div className={`flex items-center justify-${config.logo_position} h-full`}>
          {config.show_company_logo && config.header_logo_url && (
            <img 
              src={config.header_logo_url} 
              alt="Logo" 
              className="h-12 object-contain mr-4"
            />
          )}
          <h1 
            className="text-2xl font-bold"
            style={{ color: config.primary_color }}
          >
            {config.header_text}
          </h1>
        </div>
      </div>

      {/* Invoice Info */}
      <div className="p-6">
        <div className="flex justify-between mb-6">
          <div>
            <p className="font-semibold">{sampleInvoice.company_name}</p>
            {config.show_billing_address && (
              <>
                <p className="text-sm">{sampleInvoice.company_address}</p>
                <p className="text-sm">{sampleInvoice.company_city}</p>
              </>
            )}
          </div>
          <div className="text-right">
            <p className="font-semibold">Invoice #{sampleInvoice.invoice_number}</p>
            <p className="text-sm">Date: {sampleInvoice.invoice_date}</p>
            <p className="text-sm">Due: {sampleInvoice.due_date}</p>
          </div>
        </div>

        {config.show_billing_address && (
          <div className="mb-6 p-3 bg-gray-50 rounded">
            <p className="text-sm font-semibold mb-1">Bill To:</p>
            <p className="text-sm">{sampleInvoice.customer_name}</p>
            <p className="text-sm">{sampleInvoice.customer_address}</p>
          </div>
        )}

        {/* Items Table */}
        <table className="w-full mb-6">
          <thead>
            <tr style={{ backgroundColor: config.primary_color, color: 'white' }}>
              <th className="text-left p-2">Description</th>
              <th className="text-right p-2">Qty</th>
              <th className="text-right p-2">Rate</th>
              <th className="text-right p-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {sampleInvoice.items.map((item, idx) => (
              <tr key={idx} className="border-b">
                <td className="p-2">{item.description}</td>
                <td className="text-right p-2">{item.quantity}</td>
                <td className="text-right p-2">₹{item.rate}</td>
                <td className="text-right p-2">₹{item.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64 space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>₹{sampleInvoice.subtotal}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax (18%):</span>
              <span>₹{sampleInvoice.tax}</span>
            </div>
            <div 
              className="flex justify-between font-bold text-lg border-t-2 pt-2"
              style={{ borderColor: config.primary_color, color: config.primary_color }}
            >
              <span>Total:</span>
              <span>₹{sampleInvoice.total}</span>
            </div>
          </div>
        </div>

        {config.show_payment_terms && (
          <div className="mt-6 p-3 bg-gray-50 rounded">
            <p className="text-sm font-semibold mb-1">Payment Terms:</p>
            <p className="text-sm">Payment due within 30 days</p>
          </div>
        )}

        {config.show_bank_details && (
          <div className="mt-4 p-3 bg-gray-50 rounded">
            <p className="text-sm font-semibold mb-1">Bank Details:</p>
            <p className="text-sm">Bank Name: Your Bank</p>
            <p className="text-sm">Account: 1234567890</p>
            <p className="text-sm">IFSC: BANK0001234</p>
          </div>
        )}
      </div>

      {/* Footer */}
      {config.footer_text && (
        <div 
          className="p-4 text-center border-t"
          style={{ 
            height: `${config.footer_height}px`,
            borderColor: config.secondary_color,
            color: config.secondary_color
          }}
        >
          <p className="text-sm">{config.footer_text}</p>
        </div>
      )}
    </div>
  );
};

export default InvoiceTemplateDesigner;
